import React from "react";
import {
  FieldErrorContext,
  GroupContext,
  InputContext,
  LabelContext,
  Provider,
  TextAreaContext,
  TextContext,
  TextField as AriaTextField,
  type TextFieldProps,
} from "react-aria-components";
import * as styles from "./Field.css.ts";
import { InputSubtext } from "./InputSubtext.tsx";
import { Label } from "./Label.tsx";
import {
  FCTextFieldContext,
  type FCTextFieldContextInterface,
} from "./TextFieldContext.ts";
import type { OmittedReactAriaPropsWithChildren } from "./types.ts";

type BaseProps = Omit<
  Omit<TextFieldProps, "validate">,
  OmittedReactAriaPropsWithChildren
> &
  Omit<FCTextFieldContextInterface, "textFieldProps">;

export interface Props extends BaseProps {
  /** Layout orientation of the field label and input. */
  orientation?: "horizontal" | "vertical";
  /** Ref to the underlying div element. */
  ref?: React.Ref<HTMLDivElement>;
}

const Description: React.FC<{
  description?: string;
  textError?: string;
}> = ({ description, textError }) => {
  const fieldErrorContextProps = React.useMemo(
    () => ({
      isInvalid: true,
      validationErrors: textError == null ? [] : [textError],
      validationDetails: {
        customError: true,
        badInput: false,
        patternMismatch: false,
        rangeOverflow: false,
        rangeUnderflow: false,
        stepMismatch: false,
        tooLong: false,
        tooShort: false,
        typeMismatch: false,
        valid: false,
        valueMissing: false,
      },
    }),
    [textError],
  );

  if (textError != null) {
    return (
      <FieldErrorContext value={fieldErrorContextProps}>
        <InputSubtext text={description} />
      </FieldErrorContext>
    );
  }

  return <InputSubtext text={description} />;
};

/**
 * A form field container that provides label, description, and validation support.
 * Wrap Input, TextArea, or other form controls to get consistent field styling.
 * @see Input for the text input component
 * @see Label for field labels
 */
export const Field: React.FC<Props> = ({
  label,
  labelTooltip,
  description,
  orientation = "vertical",
  children,
  validate,
  labelRight,
  ref,
  ...props
}) => {
  const name = props.name;
  const textError =
    typeof validate === "string" && props.isInvalid === true
      ? validate
      : undefined;

  const ariaValidate = typeof validate === "function" ? validate : undefined;
  const labelRef = React.useRef<HTMLLabelElement>(null);

  return (
    <AriaTextField
      {...props}
      validate={ariaValidate}
      ref={ref}
      className={styles.base({ orientation })}
    >
      {(innerProps) => (
        <>
          {label != null && (
            <div className={styles.label}>
              <Label ref={labelRef} tooltip={labelTooltip}>
                {label}
              </Label>
              {labelRight != null && (
                <Provider
                  // https://github.com/adobe/react-spectrum/blob/0d96e0d0f5cf1c9082a973dbfa1c51ed5771ea14/packages/react-aria-components/src/TextField.tsx#L111-L124
                  values={[
                    [LabelContext, null],
                    [InputContext, null],
                    [TextAreaContext, null],
                    [GroupContext, null],
                    [TextContext, null],
                    [FieldErrorContext, null],
                  ]}
                >
                  {labelRight}
                </Provider>
              )}
            </div>
          )}
          <FCTextFieldContext
            value={{
              label,
              labelTooltip,
              description,
              validate,
              labelRef,
              name,
              textFieldProps: innerProps,
            }}
          >
            <div className={styles.children({ orientation })}>
              {typeof children === "function" ? children(innerProps) : children}
            </div>
          </FCTextFieldContext>
          {(description != null ||
            textError != null ||
            innerProps.isInvalid) && (
            <div className={styles.description({ orientation })}>
              <Description description={description} textError={textError} />
            </div>
          )}
        </>
      )}
    </AriaTextField>
  );
};
